import React, { useMemo } from 'react';
import { ClientSongCollection, patchClientSongCollection, uploadSongCollectionImage } from '@round/api';
import useDownloadImage from '../../../../../Hooks/useDownloadImage';
import SongCollectionForm, { SongCollectionFormProps } from '../SongCollectionForm/SongCollectionForm';
import { Modal, ModalContent, ModalTitle } from '@round/ui-kit';
import styles from './EditClientSongCollectionModal.module.css';
import { showNotification } from '../../../../../helpers';

type EditClientSongCollectionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onUpdated: (collection: ClientSongCollection) => void;
    collection: ClientSongCollection | null;
    clientId: number;
};

const EditClientSongCollectionModal = ({
    onClose,
    isOpen,
    onUpdated,
    collection,
    clientId,
}: EditClientSongCollectionModalProps) => {
    const { image, loading: imageLoading } = useDownloadImage(collection?.picture);
    const initialValues: SongCollectionFormProps['initialValues'] = useMemo(
        () => ({
            image: image ? [image] : [],
            name: collection?.name ?? '',
        }),
        [collection?.name, image]
    );

    const handleSubmit: SongCollectionFormProps['handleSubmit'] = async (values, { setSubmitting }) => {
        if (!collection) {
            return;
        }

        try {
            setSubmitting(true);
            const response = await patchClientSongCollection(clientId, collection.id, { name: values.name });

            if (response.status === 404) {
                showNotification(response.data.detail, 'error');
                return;
            }

            const [image] = values.image;
            if (!image) {
                onUpdated(response.data);
                showNotification('Song collection updated', 'info');
                return;
            }

            const imageResponse = await uploadSongCollectionImage(response.data.id, image);
            if (imageResponse.status === 404) {
                showNotification(imageResponse.data.detail, 'error');
                return;
            }

            onUpdated(imageResponse.data);
            showNotification('Song collection updated', 'info');
        } catch {
            showNotification('Could not update song collection', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={onClose} className={styles.modal}>
            <ModalTitle>Edit Song Collection</ModalTitle>
            <ModalContent>
                <SongCollectionForm
                    imageLoading={imageLoading}
                    initialValues={initialValues}
                    handleSubmit={handleSubmit}
                />
            </ModalContent>
        </Modal>
    );
};

export default EditClientSongCollectionModal;
